import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Download,
  Upload,
  ShieldAlert,
  CheckCircle2,
  AlertTriangle,
  FileJson,
  Loader2,
  Clock,
  Trophy,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import PublicShareCard from "@/components/PublicShareCard";
import { Link } from "react-router-dom";
import { CreditCard } from "lucide-react";

// Settings is the catch-all admin console for things that don't belong on
// the day-to-day data pages. Right now it hosts Backup & Restore; future
// additions (notification preferences, branding, etc.) can slot in as new
// sections.
export default function Settings() {
  const { user } = useAuth();

  if (user?.role !== "ADMIN" && user?.role !== "SUPERADMIN") {
    return (
      <div className="p-4 md:p-8 max-w-3xl">
        <p className="muted-text">Only administrators can access settings.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl space-y-10" data-testid="settings-page">
      <div>
        <div className="ccs-label mb-2">Administration</div>
        <h1 className="text-4xl font-semibold tracking-tight">Settings</h1>
        <p className="muted-text mt-2 text-sm">
          One-click data backup, restore, and other club-wide configuration.
        </p>
      </div>

      <BackupSection />
      <RestoreSection />
      <ClubBrandingSection />
      <PlanSection />
      <ScoringRulesSection />
      <section>
        <div className="ccs-label mb-3">Public results link</div>
        <PublicShareCard />
      </section>
    </div>
  );
}


/** Pulled out so both the banner and the success message can share it. */
function useBackupStatus() {
  const [status, setStatus] = useState(null);
  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/backup/status");
      setStatus(data);
    } catch (_) {
      setStatus(null);
    }
  };
  useEffect(() => { refresh(); }, []);
  return { status, refresh };
}


function BackupSection() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stamp, setStamp] = useState("");
  const { status, refresh } = useBackupStatus();

  // We use raw fetch (not axios) because axios JSON-decodes the response
  // before we get a chance to download it — fetch lets us stream the file
  // straight to disk via a Blob URL.
  const download = async () => {
    setBusy(true);
    setErr("");
    try {
      const token = localStorage.getItem("ccs_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Backup failed (HTTP ${res.status}): ${txt.slice(0, 200)}`);
      }
      // Filename comes from the Content-Disposition header so it matches
      // what the server thinks it called the file.
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `camera-club-backup-${new Date().toISOString().slice(0, 10)}.ccs-backup.json`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStamp(new Date().toLocaleString());
      refresh();
    } catch (ex) {
      setErr(ex.message || "Couldn't download the backup.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ccs-card" data-testid="settings-backup-section">
      {status?.needs_reminder && <BackupReminderBanner status={status} />}

      <div className="flex items-start gap-3 mb-4">
        <Download className="text-amber-400 mt-0.5 shrink-0" size={20} />
        <div className="flex-1">
          <h2 className="text-xl font-semibold mb-1">Backup your club</h2>
          <p className="muted-text text-sm leading-relaxed">
            Download a single JSON file containing every photographer, season,
            competition, entry, and team member in your club. Keep a copy on your
            laptop (or a Dropbox / Google Drive folder) and you&rsquo;ll be able
            to recover from accidental deletions in a single click.
          </p>
        </div>
      </div>

      <ul className="text-xs muted-text mb-5 space-y-1 ml-1">
        <li>✓ Includes scores, placements, and image titles.</li>
        <li>✓ Includes team members (email + role) — passwords are NEVER exported, so the file is safe to email.</li>
        <li>✗ Pending invitations and public-share tokens are not exported (they&rsquo;d be stale).</li>
      </ul>

      <button
        onClick={download}
        disabled={busy}
        className="ccs-btn flex items-center gap-2"
        data-testid="backup-download-button"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileJson size={14} />}
        {busy ? "Preparing backup…" : "Download backup"}
      </button>

      {status?.last_backup_at && (
        <div className="muted-text text-xs mt-3 flex items-center gap-1" data-testid="backup-last-stamp">
          <Clock size={12} /> Last backup taken{" "}
          {status.days_ago === 0
            ? "today"
            : status.days_ago === 1
            ? "1 day ago"
            : `${status.days_ago} days ago`}
          {status.last_backup_by && ` by ${status.last_backup_by}`}
        </div>
      )}

      {stamp && (
        <div className="success-text text-xs mt-3 flex items-center gap-1" data-testid="backup-success">
          <CheckCircle2 size={12} /> Backup downloaded at {stamp}
        </div>
      )}
      {err && (
        <div className="danger-text text-xs mt-3" data-testid="backup-error">{err}</div>
      )}
    </section>
  );
}


function BackupReminderBanner({ status }) {
  const never = status.last_backup_at == null;
  const line = never
    ? "You haven't taken a backup yet."
    : `It's been ${status.days_ago} days since your last backup.`;
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 mb-5 text-sm"
      style={{ background: "#1d160a", border: "1px solid #3a2a10", color: "#fbbf24" }}
      data-testid="backup-reminder-banner"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-medium">{line}</div>
        <div className="muted-text text-xs mt-0.5">
          Take 30 seconds now to download a fresh copy — it&rsquo;s the
          safety net if something ever gets accidentally deleted.
        </div>
      </div>
    </div>
  );
}


function RestoreSection() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setConfirmName("");
    setMode("merge");
    setResult(null);
    setErr("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setResult(null);
    if (!file) {
      setErr("Please choose a .ccs-backup.json file first.");
      return;
    }
    if (mode === "replace" && !confirmName.trim()) {
      setErr("Type your club name to confirm a destructive restore.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (mode === "replace") fd.append("confirm_club_name", confirmName);
      const { data } = await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const totalInserted = result
    ? Object.values(result.inserted || {}).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <section className="ccs-card" data-testid="settings-restore-section">
      <div className="flex items-start gap-3 mb-4">
        <Upload className="text-amber-400 mt-0.5 shrink-0" size={20} />
        <div className="flex-1">
          <h2 className="text-xl font-semibold mb-1">Restore from a backup</h2>
          <p className="muted-text text-sm leading-relaxed">
            Re-upload a previously downloaded <span className="mono text-xs">.ccs-backup.json</span> file
            to recover data. Use <b className="text-amber-400">Merge</b> for everyday recovery, or{" "}
            <b className="text-red-400">Replace</b> when you want to wipe the current club state and
            reload everything from scratch.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="form-row">
          <label className="ccs-label">Backup file</label>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="ccs-input"
            data-testid="restore-file-input"
          />
          {file && (
            <div className="text-xs muted-text mt-1" data-testid="restore-file-info">
              Selected: <span className="mono">{file.name}</span> ({Math.ceil(file.size / 1024)} KB)
            </div>
          )}
        </div>

        <div className="form-row">
          <label className="ccs-label">Mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className={
                "border p-3 cursor-pointer flex items-start gap-2 " +
                (mode === "merge" ? "border-amber-400" : "border-neutral-800")
              }
              data-testid="restore-mode-merge"
            >
              <input
                type="radio"
                checked={mode === "merge"}
                onChange={() => setMode("merge")}
                className="accent-amber-400 mt-1"
              />
              <div>
                <div className="text-sm font-medium">Merge (safe)</div>
                <div className="text-xs muted-text mt-0.5">
                  Adds anything missing. Existing rows with the same ID are kept untouched.
                </div>
              </div>
            </label>

            <label
              className={
                "border p-3 cursor-pointer flex items-start gap-2 " +
                (mode === "replace" ? "border-red-400" : "border-neutral-800")
              }
              data-testid="restore-mode-replace"
            >
              <input
                type="radio"
                checked={mode === "replace"}
                onChange={() => setMode("replace")}
                className="accent-red-400 mt-1"
              />
              <div>
                <div className="text-sm font-medium text-red-400 flex items-center gap-1">
                  <ShieldAlert size={14} /> Replace all (destructive)
                </div>
                <div className="text-xs muted-text mt-0.5">
                  Wipes your photographers, seasons, competitions, entries and
                  team members (except yourself), then reloads from the file.
                </div>
              </div>
            </label>
          </div>
        </div>

        {mode === "replace" && (
          <div className="form-row" data-testid="restore-confirm-row">
            <label className="ccs-label">
              Type your club name to confirm
            </label>
            <input
              className="ccs-input mono"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Exact club name as shown in the top bar"
              data-testid="restore-confirm-input"
            />
            <div className="muted-text text-xs mt-1">
              This belt-and-braces step makes sure a destructive restore can&rsquo;t be a clicking accident.
            </div>
          </div>
        )}

        {err && (
          <div className="danger-text text-sm flex items-start gap-2" data-testid="restore-error">
            <AlertTriangle size={14} className="mt-0.5" /> {err}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={busy || !file}
            className={
              "flex items-center gap-2 " +
              (mode === "replace" ? "ccs-btn-danger" : "ccs-btn")
            }
            data-testid="restore-submit-button"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy
              ? "Restoring…"
              : mode === "replace"
              ? "Wipe and restore"
              : "Restore (merge)"}
          </button>
          {(file || result) && (
            <button type="button" onClick={reset} className="ccs-btn-ghost" data-testid="restore-reset">
              Reset
            </button>
          )}
        </div>
      </form>

      {result && (
        <div
          className="mt-5 p-4"
          style={{ background: "#10261a", border: "1px solid #1f4a30" }}
          data-testid="restore-result"
        >
          <div className="flex items-center gap-2 success-text font-medium">
            <CheckCircle2 size={16} /> Restore complete — {totalInserted} new row(s) added.
          </div>
          <RestoreSummaryTable summary={result} />
          {result.warnings?.length > 0 && (
            <ul className="text-xs text-amber-300 mt-3 space-y-1">
              {result.warnings.map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}


function RestoreSummaryTable({ summary }) {
  const sections = ["photographers", "seasons", "competitions", "entries", "users"];
  return (
    <table className="ccs-table mt-3 text-xs">
      <thead>
        <tr>
          <th>Collection</th>
          <th className="text-right">Inserted</th>
          <th className="text-right">Skipped</th>
          {summary.mode === "replace" && <th className="text-right">Wiped first</th>}
        </tr>
      </thead>
      <tbody>
        {sections.map((s) => (
          <tr key={s}>
            <td className="capitalize">{s}</td>
            <td className="text-right mono">{summary.inserted?.[s] ?? 0}</td>
            <td className="text-right mono muted-text">{summary.skipped?.[s] ?? 0}</td>
            {summary.mode === "replace" && (
              <td className="text-right mono muted-text">{summary.deleted?.[s] ?? "—"}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}


/* ============================================================
   Scoring rules — how Photographer of the Year is calculated.
   Three modes, default "all" (no behaviour change for existing clubs).
   ============================================================ */

const SCORING_MODES = [
  {
    value: "all",
    label: "Every entry counts",
    blurb: "Every image scored across the season is added to a photographer's total. Use this if you want each photo to contribute regardless of how many a photographer entered per round.",
    example: "3 entries scoring 10, 7, 5 → total 22",
  },
  {
    value: "top1",
    label: "Best image per competition",
    blurb: "For each competition, only the photographer's HIGHEST-scoring image contributes to the season total. Common for one-entry-per-round formats.",
    example: "3 entries scoring 10, 7, 5 → counts 10",
  },
  {
    value: "top2",
    label: "Top two images per competition",
    blurb: "For each competition, the photographer's TOP TWO highest scores are added together. Common when members can submit a pair per round.",
    example: "3 entries scoring 10, 7, 5 → counts 17",
  },
];


function ScoringRulesSection() {
  const [club, setClub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedMode, setSavedMode] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clubs/me");
        setClub(data);
      } catch (ex) {
        setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, []);

  const current = (club?.poy_scoring_mode || "all");

  const pick = async (mode) => {
    if (mode === current) return;
    setBusy(true);
    setErr("");
    setSavedMode("");
    try {
      const { data } = await api.patch("/clubs/me", { poy_scoring_mode: mode });
      setClub(data);
      setSavedMode(mode);
      setTimeout(() => setSavedMode(""), 4000);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ccs-card" data-testid="settings-scoring-section">
      <div className="flex items-start gap-3 mb-4">
        <Trophy className="text-amber-400 mt-0.5 shrink-0" size={20} />
        <div className="flex-1">
          <h2 className="text-xl font-semibold mb-1">Photographer of the Year — scoring rules</h2>
          <p className="muted-text text-sm leading-relaxed">
            Different camera clubs total their season scores differently.
            Pick the rule that matches how your club has always done it —
            this only affects the Photographer-of-the-Year leaderboard and
            the multi-season trend page.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {SCORING_MODES.map((m) => {
          const active = current === m.value;
          return (
            <button
              type="button"
              key={m.value}
              onClick={() => pick(m.value)}
              disabled={busy}
              className="text-left p-4 transition-colors disabled:opacity-60"
              style={{
                background: active ? "#1d160a" : "#0f0f0f",
                border: active ? "1px solid #fbbf24" : "1px solid #1f1f1f",
              }}
              data-testid={`scoring-mode-${m.value}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={"text-sm font-medium " + (active ? "text-amber-400" : "")}>
                  {m.label}
                </span>
                {active && <CheckCircle2 size={14} className="text-amber-400" />}
              </div>
              <p className="text-xs muted-text leading-relaxed mb-3">{m.blurb}</p>
              <div className="text-xs mono" style={{ color: active ? "#fbbf24" : "#666" }}>
                e.g. {m.example}
              </div>
            </button>
          );
        })}
      </div>

      {savedMode && (
        <div className="success-text text-xs mt-4 flex items-center gap-1" data-testid="scoring-mode-saved">
          <CheckCircle2 size={12} /> Saved — Photographer of the Year now uses &ldquo;
          {SCORING_MODES.find((m) => m.value === savedMode)?.label}&rdquo;.
        </div>
      )}
      {err && (
        <div className="danger-text text-xs mt-4 flex items-center gap-1">
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <div className="muted-text text-xs mt-5 pt-4" style={{ borderTop: "1px solid #1f1f1f" }}>
        <b>Note:</b> Placement counts (1st / 2nd / 3rd / Highly Commended /
        Commended) are not affected by this setting — they always reflect
        what the judge awarded on the night.
      </div>
    </section>
  );
}



// Club branding — upload a logo that appears in the top header, on the
// public portal, and in exported PDFs. Stored as a base64 data URL on
// the club doc so we don't need a separate object store — logos are
// tiny (typically 50–150 KB) and MongoDB handles this trivially.
function ClubBrandingSection() {
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/clubs/me");
      setLogoUrl(data?.logo_data_url || "");
    } catch (_) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same filename
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/clubs/me/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.logo_data_url || "");
      window.dispatchEvent(new CustomEvent("ccs:club-updated"));
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the club logo?")) return;
    setBusy(true);
    setError("");
    try {
      await api.delete("/clubs/me/logo");
      setLogoUrl("");
      window.dispatchEvent(new CustomEvent("ccs:club-updated"));
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ccs-card" data-testid="settings-branding-section">
      <div className="flex items-start gap-3 mb-4">
        <ImageIcon className="text-amber-400 mt-0.5 shrink-0" size={20} />
        <div className="flex-1">
          <h2 className="text-xl font-semibold mb-1">Club branding</h2>
          <p className="muted-text text-sm">
            Upload your club logo. It appears at the top of every page, on
            your public portal, and in exported PDFs. PNG, JPG, SVG or WebP,
            up to 500 KB.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div
          className="w-24 h-24 border border-neutral-800 bg-neutral-900 flex items-center justify-center overflow-hidden"
          data-testid="branding-preview"
        >
          {loading ? (
            <Loader2 className="animate-spin muted-text" size={16} />
          ) : logoUrl ? (
            <img
              src={logoUrl}
              alt="Club logo preview"
              className="max-w-full max-h-full object-contain"
              data-testid="branding-preview-image"
            />
          ) : (
            <div className="muted-text text-xs text-center px-2">No logo</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={onFile}
            hidden
            data-testid="branding-file-input"
          />
          <button
            onClick={pick}
            disabled={busy}
            className="ccs-btn text-sm flex items-center gap-2"
            data-testid="branding-upload-button"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {logoUrl ? "Replace logo" : "Upload logo"}
          </button>
          {logoUrl && !busy && (
            <button
              onClick={remove}
              className="ccs-btn-ghost text-sm flex items-center gap-2 danger-text"
              data-testid="branding-remove-button"
            >
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 text-sm danger-text" data-testid="branding-error">
          {error}
        </div>
      )}
    </section>
  );
}



function PlanSection() {
  // Small at-a-glance card so admins see their plan tier + renewal date
  // without opening /billing. Hidden entirely if paywall is off (nothing
  // meaningful to show) or the club is grandfathered on the beta plan.
  const [status, setStatus] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        setStatus(data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!status || !status.paywall_enabled) return null;
  if (status.plan_tier === "grandfathered") return null;

  const tierLabel = {
    trial: "Free trial",
    starter: "Starter",
    unlimited: "Unlimited",
    expired: "Expired",
  }[status.plan_tier] || status.plan_tier;

  const price = {
    starter: "£60/year",
    unlimited: "£80/year",
  }[status.plan_tier] || "";

  const expires = status.plan_expires_at
    ? new Date(status.plan_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const days = status.days_remaining;
  const soon = typeof days === "number" && days >= 0 && days <= 7;
  const past = typeof days === "number" && days <= 0;

  return (
    <section data-testid="settings-plan-section">
      <div className="ccs-label mb-3">Plan &amp; billing</div>
      <div className="ccs-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <CreditCard size={18} className="text-amber-400" />
              <span className="text-lg font-semibold">
                {tierLabel}
                {price && <span className="muted-text text-sm ml-2">{price}</span>}
              </span>
            </div>
            {expires && (
              <div className={"text-sm " + (past ? "text-red-400" : soon ? "text-amber-400" : "muted-text")}>
                {status.plan_tier === "trial"
                  ? (past ? "Trial ended " : "Trial ends ")
                  : (past ? "Ended " : "Renews ")}
                <span className="mono">{expires}</span>
                {typeof days === "number" && !past && (
                  <span className="muted-text ml-2">&middot; {days} day{days === 1 ? "" : "s"} remaining</span>
                )}
              </div>
            )}
            {status.photographer_limit && (
              <div className="text-xs muted-text mt-2">
                Using <span className="mono text-white">{status.photographer_count}</span>
                {" of "}
                <span className="mono text-white">{status.photographer_limit}</span>
                {" photographers on the "}{tierLabel} plan.
              </div>
            )}
          </div>
          <Link to="/billing" className="ccs-btn-ghost text-sm" data-testid="settings-plan-manage">
            Manage billing &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}

